import type { ReploIntegration } from "schemas/generated/integration";
import type { z } from "zod";

import * as React from "react";

import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@replo/design-system/components/button/Button";
import { Input } from "@replo/design-system/components/input/Input";
import { toast } from "@replo/design-system/components/toast/Toast";
import DeleteConfirmationModal from "@v2/components/DeleteConfirmationModal";
import { useCurrentWorkspaceId } from "@v2/contexts/WorkspaceDashboardContext";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { useLogAnalytics } from "@v2/hooks/useLogAnalytics";
import {
  integrationRouter,
  integrationUtils,
} from "@v2/trpc/routes/integration";
import { useForm } from "react-hook-form";

import { INTEGRATION_CONFIGS } from "./config";

export type IntegrationType = Exclude<
  ReploIntegration["type"],
  "ShopifyIntegrationConfig" | "StripeConnect"
>;

type IntegrationFormData<T extends IntegrationType> = z.infer<
  (typeof INTEGRATION_CONFIGS)[T]["schema"]
>;

export const IntegrationForm: React.FC<{
  integration: IntegrationType;
  handleCancel: () => void;
  initialValues?: IntegrationFormData<IntegrationType>;
  integrationId?: string;
}> = ({ integration, handleCancel, initialValues, integrationId }) => {
  const logEvent = useLogAnalytics();
  const { title, description, image } = INTEGRATION_CONFIGS[integration];

  const projectId = useCurrentProjectId();
  const workspaceId = useCurrentWorkspaceId();

  const doesIntegrationExist = Boolean(integrationId);
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const { mutate: createIntegration, isPending: isCreatingIntegration } =
    integrationRouter.createOrUpdate.useMutation({
      onSuccess: () => {
        if (doesIntegrationExist) {
          toast.success({
            title: "Integration Updated",
            description: "Your integration has been updated successfully.",
          });
        } else {
          toast.success({
            title: "Integration Created",
            description: "Your integration has been created successfully.",
          });
        }

        void integrationUtils.find.invalidate();
        void integrationUtils.get.invalidate();
        handleCancel();
      },
      onError: () => {
        if (doesIntegrationExist) {
          toast.error({
            title: "Error Updating Integration",
            description: "There was an error updating your integration.",
          });
        } else {
          toast.error({
            title: "Error Creating Integration",
            description: "There was an error creating your integration.",
          });
        }
      },
    });

  const { mutate: deleteIntegration, isPending: isDeletingIntegration } =
    integrationRouter.deleteIntegrationFromProject.useMutation({
      onSuccess: () => {
        toast.success({
          title: "Integration Disconnected",
          description: "Your integration has been disconnected successfully.",
        });
        // Navigate immediately
        handleCancel();
        // Only invalidate the list query to refresh the integrations list
        // Don't invalidate integration.get since we just deleted it
        void integrationUtils.find.invalidate();
      },
      onError: () => {
        toast.error({
          title: "Error Disconnecting Integration",
          description: "There was an error disconnecting your integration.",
        });
      },
    });

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<IntegrationFormData<typeof integration>>({
    resolver: zodResolver(INTEGRATION_CONFIGS[integration].schema),
    defaultValues: initialValues,
  });

  const onSubmit = handleSubmit(
    (data: IntegrationFormData<typeof integration>) => {
      logEvent("integrations.add", {
        integration: integration,
      });
      createIntegration({
        type: integration,
        config: data,
        workspaceId: workspaceId ?? "",
        projectId: projectId ?? "",
        id: integrationId,
      });
    },
  );

  /**
   * NOTE (Max, 2025-07-31): The fields on the IntegrationForm are dynamically generated based on the integration type.
   * That's because the fields required for Klaviyo are different from e.g. Hubspot. To figure out which fields
   * to generate, we look at INTEGRATION_CONFIGS.
   *
   * We use the functions provided by useForm to register the fields and show errors.
   */
  const renderFields = () => {
    const fields = INTEGRATION_CONFIGS[integration].fields;
    return (
      <>
        {Object.entries(fields).map(([fieldName, fieldConfig]) => {
          const errorMessage = errors[fieldName as keyof typeof errors];
          const helpUrl = "helpUrl" in fieldConfig ? fieldConfig.helpUrl : null;
          const helpText =
            "helpText" in fieldConfig ? fieldConfig.helpText : null;

          return (
            <div key={fieldName} className="space-y-1">
              <span className="text-xs font-medium break-words">
                {fieldConfig.label}
              </span>
              <Input
                {...register(
                  fieldName as keyof IntegrationFormData<typeof integration>,
                )}
                placeholder={fieldConfig.placeholder}
                size="base"
              />
              {helpUrl && helpText && (
                <Button
                  variant="link"
                  size="sm"
                  onClick={() => {
                    return window.open(
                      helpUrl,
                      "_blank",
                      "noopener,noreferrer",
                    );
                  }}
                >
                  {helpText}
                </Button>
              )}
              {errorMessage && (
                <p className="text-xs text-rose break-words">
                  {errorMessage.message}
                </p>
              )}
            </div>
          );
        })}
      </>
    );
  };

  return (
    <div className="border-0.5 border-border p-4 rounded-xl flex flex-col gap-4">
      <div className="flex flex-row gap-3">
        <img
          src={image}
          alt={title}
          style={{
            width: "36px",
            height: "36px",
          }}
          className="contain rounded-sm"
        />
        <div className="flex flex-col">
          <span className="header-h6 break-words">{title}</span>
          <span className="text-sm text-muted break-words leading-relaxed">
            {description}
          </span>
        </div>
      </div>

      <div className="space-y-4">{renderFields()}</div>

      <div className="flex flex-row justify-between gap-3">
        {doesIntegrationExist && (
          <Button
            variant="secondary"
            size="base"
            onClick={() => setShowDeleteModal(true)}
          >
            Disconnect
          </Button>
        )}
        <div className="flex flex-row gap-3">
          <Button variant="secondary" size="base" onClick={handleCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="base"
            loading={isCreatingIntegration}
            onClick={() => void onSubmit()}
          >
            Save Changes
          </Button>
        </div>
      </div>

      {showDeleteModal && (
        <DeleteConfirmationModal
          assetName={title}
          assetTypeDisplayName="Integration"
          confirmationType="delete"
          extraMessage="This action cannot be undone."
          onDelete={() => {
            deleteIntegration({
              projectId: projectId ?? "",
              integrationId: integrationId ?? "",
            });
            setShowDeleteModal(false);
          }}
          onRequestClose={() => setShowDeleteModal(false)}
          isLoading={isDeletingIntegration}
        />
      )}
    </div>
  );
};
